export const initialState: MBChatState = {
  cachedMessages: {},
  deliveryStatusFromMessageUuid: {},
  cachedMessageUuidsFromChannelSlug: {},
  layoutOptionsFromMessageId: {},
  messageDraftFromChannelSlug: {},
  messageBeingEditedFromDraftId: {},
};

export const emptyDraft: MBChatMessageDraft = {
  content: '',
  urgent: false,
  editing: null,
  attachments: [],
};

export function chatMessagesReducer(
  state: MBChatState = initialState,
  action: ChatMessagesAction,
): MBChatState {
  switch (action.type) {
    case 'CHAT:UPDATE_MESSAGE_LAYOUT': {
      return {
        ...state,
        layoutOptionsFromMessageId: {
          ...state.layoutOptionsFromMessageId,
          ...action.value.layoutOptions,
        },
      };
    }

    case 'CHAT:ADD_MESSAGE_TO_CACHE': {
      const { channelSlug, message } = action.value;
      if (!message.meta.uuid) {
        return state;
      }
      return {
        ...state,
        cachedMessages: { ...state.cachedMessages, [message.meta.uuid]: message },
        cachedMessageUuidsFromChannelSlug: {
          ...state.cachedMessageUuidsFromChannelSlug,
          [channelSlug]: [
            ...(state.cachedMessageUuidsFromChannelSlug[channelSlug] || []),
            message.meta.uuid,
          ],
        },
      };
    }

    case 'CHAT:REMOVE_MESSAGE_FROM_CACHE': {
      const { channelSlug, message } = action.value;
      if (!message.meta.uuid) {
        return state;
      }
      return {
        ...state,
        // remove the cached message
        cachedMessages: { ...state.cachedMessages, [message.meta.uuid]: undefined },
        // remove it's delivery status (simply for cleanup)
        deliveryStatusFromMessageUuid: {
          ...state.deliveryStatusFromMessageUuid,
          [message.meta.uuid]: undefined,
        },
        // remove its association with the channel
        cachedMessageUuidsFromChannelSlug: {
          ...state.cachedMessageUuidsFromChannelSlug,
          [channelSlug]: (state.cachedMessageUuidsFromChannelSlug[channelSlug] || []).filter(
            uuid => message.meta.uuid !== uuid,
          ),
        },
      };
    }

    case 'CHAT:UPDATE_MESSAGE_DELIVERY_STATUS': {
      const { message, status } = action.value;
      if (!message.meta.uuid) {
        return state;
      }
      return {
        ...state,
        deliveryStatusFromMessageUuid: {
          ...state.deliveryStatusFromMessageUuid,
          [message.meta.uuid]: status,
        },
      };
    }

    case 'CHAT:PERSIST_MESSAGES_FROM_STORAGE': {
      const persistedState: {
        cachedMessages: Dict<MBAPI.ChatMessage[]>;
        cachedMessageUuidsFromChannelSlug: Dict<string[]>;
        deliveryStatusFromMessageUuid: Dict<MBDeliveryStatus>;
      } = action.value;
      return {
        ...state,
        ...persistedState,
      };
    }

    /////////////////////////// MESSAGE DRAFT ///////////////////////////
    case 'CHAT:UPDATE_DRAFT_CONTENT': {
      const { content, channelSlug } = action.value;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: { ...draft, content },
        },
      };
    }

    case 'CHAT:UPDATE_DRAFT_URGENT': {
      const { urgent, channelSlug } = action.value;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: { ...draft, urgent },
        },
      };
    }

    case 'CHAT:UPDATE_DRAFT_EDITING': {
      const { editing, channelSlug } = action.value;
      const { content, urgent } = editing;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: { ...draft, editing, content, urgent },
        },
      };
    }

    case 'CHAT:ADD_ATTACHMENT_TO_DRAFT': {
      const { attachment, channelSlug } = action.value;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: {
            ...draft,
            attachments: [...draft.attachments, attachment],
          },
        },
      };
    }

    case 'CHAT:REMOVE_ATTACHMENT_FROM_DRAFT': {
      const { attachment, channelSlug } = action.value;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: {
            ...draft,
            attachments: draft.attachments.filter(a => a.fileKey !== attachment.fileKey),
          },
        },
      };
    }

    case 'CHAT:REMOVE_ALL_ATTACHMENTS_FROM_DRAFT': {
      const { channelSlug } = action.value;
      const draft = state.messageDraftFromChannelSlug[channelSlug] || emptyDraft;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: {
            ...draft,
            attachments: [],
          },
        },
      };
    }

    case 'CHAT:REMOVE_DRAFT': {
      const { channelSlug } = action.value;
      return {
        ...state,
        messageDraftFromChannelSlug: {
          ...state.messageDraftFromChannelSlug,
          [channelSlug]: undefined,
        },
      };
    }

    case 'CHAT:UPDATE_EDITING_MESSAGE': {
      const { message, draftId } = action.value;
      return {
        ...state,
        messageBeingEditedFromDraftId: {
          ...state.messageBeingEditedFromDraftId,
          [draftId]: message,
        },
      };
    }

    default:
      return state;
  }
}
